import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    input,
    model,
    signal,
    ViewEncapsulation,
} from '@angular/core';

// ─── Types ───────────────────────────────────────────────────────────────────

/**
 * Documento associado a um passo (retornado no `end_step`). Exibição fora de
 * escopo por ora — mantido por completude do contrato.
 */
export type StepDoc = {
    name: string;
    num_chunks?: number;
    message?: string;
};

/**
 * Dados (`data`) de um evento de passo de processamento.
 *
 * - `name`: identificador do passo (casa `start_step` com `end_step`).
 * - `dateStart`/`dateEnd`: instantes ISO (o `dateEnd` só vem no `end_step`).
 * - `message`: mensagem informativa exibida ao usuário.
 * - `docs`: documentos do `end_step` (exibição fora de escopo por ora).
 */
export type StepEventData = {
    name: string;
    dateStart: string;
    dateEnd?: string;
    message?: string;
    docs?: StepDoc[];
};

/** Evento de passo de processamento (cru), como emitido pelo backend. */
export type StepEvent = {
    event: 'start_step' | 'end_step';
    data: StepEventData;
};

/**
 * Passo já consolidado para exibição (mescla de `start_step` + `end_step` por
 * `name`).
 */
export type DisplayStep = {
    name: string;
    /** Mensagem do `start_step` (texto principal, em negrito). */
    message: string;
    /** Mensagem do `end_step` (exibida abaixo, em itálico), se houver. */
    endMessage?: string;
    /** Classe do ícone (Font Awesome) escolhido a partir do `name`/`message`. */
    icon: string;
    /** `true` enquanto não houver `end_step` correspondente. */
    running: boolean;
    /** Tempo decorrido em ms (`dateEnd - dateStart`), ou `null` se em andamento. */
    elapsedMs: number | null;
};

/** Passo pronto para renderizar (com o texto parcial do efeito de digitação). */
export type RenderedStep = DisplayStep & {
    /** Texto atualmente visível (revelado letra a letra no último passo). */
    text: string;
    /** `true` para o último passo da lista (exibido em negrito). */
    last: boolean;
};

/**
 * Regra de mapeamento de passo → ícone. As `keywords` são casadas (substring,
 * minúsculas) primeiro contra o `name` do passo e, em seguida, contra a
 * `message`. A primeira regra que casar define o ícone.
 */
type IconRule = { keywords: string[]; icon: string };

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Bloco colapsável que exibe os **passos de processamento** de uma mensagem do
 * assistente (ex.: `bm25_search`) como uma linha do tempo: cada passo tem um
 * ícone (escolhido pelo `name`/`message`), a `message` e o tempo decorrido
 * (`dateEnd - dateStart`) — ou um indicador de "em andamento".
 *
 * O **último passo** é exibido em negrito e tem a `message` revelada com um
 * efeito de **digitação** (uma letra por vez); os passos anteriores aparecem já
 * completos. O passo em andamento aparece em itálico.
 *
 * Recebe os eventos crus (`start_step`/`end_step`) e os **consolida por `name`**.
 * O estado de expansão é controlado por {@link expanded} (two-way). Componente
 * apenas de apresentação — não traduz textos (`heading` e os rótulos do toggle
 * chegam já traduzidos).
 */
@Component({
    selector: 'ai-steps',
    standalone: true,
    templateUrl: './steps.component.html',
    styleUrls: ['./steps.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepsComponent {
    /**
     * Regras de ícone por palavra-chave, da mais específica para a mais genérica.
     * Casadas contra `name` e depois `message` (substring, minúsculas). Usa
     * ícones da biblioteca Font Awesome (`fa-solid`), padrão do projeto.
     */
    private static readonly ICON_RULES: IconRule[] = [
        { keywords: ['done', 'finish', 'end', 'ready', 'pronto', 'concl', 'final', 'complete'], icon: 'fa-circle-check' },
        { keywords: ['bm25', 'keyword', 'lexical', 'palavra'], icon: 'fa-magnifying-glass' },
        { keywords: ['knn', 'vector', 'vetor', 'semantic', 'semânt', 'semant', 'embed', 'dense'], icon: 'fa-diagram-project' },
        { keywords: ['rerank', 'rank', 'reorder', 'reorden', 'sort', 'orden'], icon: 'fa-arrow-down-wide-short' },
        { keywords: ['retriev', 'fetch', 'database', 'index', 'busca', 'search'], icon: 'fa-magnifying-glass' },
        { keywords: ['rewrite', 'expand', 'reformul', 'query', 'consulta'], icon: 'fa-wand-magic-sparkles' },
        { keywords: ['filter', 'filtr'], icon: 'fa-filter' },
        { keywords: ['web', 'internet', 'browse', 'crawl', 'url'], icon: 'fa-globe' },
        { keywords: ['generat', 'gerar', 'answer', 'respond', 'response', 'respost', 'llm', 'model'], icon: 'fa-comment-dots' },
        { keywords: ['think', 'reason', 'racioc', 'analy', 'anális', 'analis', 'reflex', 'reflet'], icon: 'fa-brain' },
        { keywords: ['start', 'init', 'begin', 'inici'], icon: 'fa-bolt' },
    ];

    /** Ícone padrão quando nenhuma regra casa. */
    private static readonly DEFAULT_ICON = 'fa-gear';

    /** Intervalo (ms) entre caracteres do efeito de digitação. */
    private static readonly TYPING_INTERVAL_MS = 18;

    private readonly destroyRef = inject(DestroyRef);

    /** Eventos crus de passos (start_step/end_step) a consolidar e exibir. */
    readonly steps = input<StepEvent[]>([]);

    /** Rótulo do cabeçalho (já traduzido pelo container). */
    readonly heading = input<string>('');

    /** Rótulo do toggle quando expandido (ex.: "Esconder detalhes"), já traduzido. */
    readonly expandedLabel = input<string>('');

    /** Rótulo do toggle quando colapsado (ex.: "Mostrar detalhes"), já traduzido. */
    readonly collapsedLabel = input<string>('');

    /** Estado de expansão do bloco (two-way). */
    readonly expanded = model<boolean>(false);

    /** Quantidade de caracteres já "digitados" por passo (chave = `name`). */
    private readonly typedLengths = new Map<string, number>();

    /** Contador que força o recálculo de {@link renderedSteps} a cada caractere. */
    private readonly typingTick = signal(0);

    /** Handle do timer de digitação (ativo apenas enquanto há texto a revelar). */
    private typingTimer: ReturnType<typeof setInterval> | null = null;

    constructor() {
        this.destroyRef.onDestroy(() => this.stopTyping());
        // Inicia/mantém a digitação sempre que surgem novos passos/texto no
        // último passo (a dependência de `displaySteps` reexecuta o efeito).
        effect(() => {
            const steps = this.displaySteps();
            const last = steps[steps.length - 1];
            if (last && this.typedCount(last) < last.message.length) {
                this.startTyping();
            }
        });
    }

    /**
     * Passos consolidados por `name`, na ordem do primeiro `start_step`. O
     * `start_step` define `message`/início; o `end_step` fecha o passo e calcula
     * o tempo decorrido. O ícone é derivado do `name`/`message`.
     */
    protected readonly displaySteps = computed<DisplayStep[]>(() => {
        type Working = DisplayStep & { dateStart?: string; dateEnd?: string };
        const byName = new Map<string, Working>();
        const order: string[] = [];

        for (const event of this.steps()) {
            const data = event?.data;
            if (!data?.name) {
                continue;
            }
            let step = byName.get(data.name);
            if (!step) {
                step = {
                    name: data.name,
                    message: '',
                    endMessage: undefined,
                    icon: StepsComponent.DEFAULT_ICON,
                    running: true,
                    elapsedMs: null,
                };
                byName.set(data.name, step);
                order.push(data.name);
            }

            if (event.event === 'start_step') {
                step.message = data.message ?? step.message;
                step.dateStart = data.dateStart;
                step.running = step.dateEnd == null;
            } else if (event.event === 'end_step') {
                step.dateEnd = data.dateEnd;
                step.running = false;
                // Mensagem de fim: topo do `data` ou o primeiro doc que a tiver.
                const endMessage =
                    data.message ??
                    data.docs?.find((doc) => doc?.message)?.message;
                if (endMessage) {
                    step.endMessage = endMessage;
                }
                // Sem `start_step`: usa a mensagem de fim como texto principal.
                if (!step.message) {
                    step.message = step.endMessage ?? '';
                }
                const start = step.dateStart ?? data.dateStart;
                if (start && data.dateEnd) {
                    step.elapsedMs =
                        new Date(data.dateEnd).getTime() -
                        new Date(start).getTime();
                }
            }
        }

        return order.map((name) => {
            const { dateStart: _s, dateEnd: _e, ...display } = byName.get(name)!;
            display.icon = this.iconFor(display.name, display.message);
            return display;
        });
    });

    /**
     * Passos prontos para renderizar. Os passos anteriores aparecem com o texto
     * completo; o **último** tem o texto revelado progressivamente (efeito de
     * digitação), controlado por {@link typingTick}.
     */
    protected readonly renderedSteps = computed<RenderedStep[]>(() => {
        this.typingTick(); // dependência: recomputa a cada caractere digitado
        const steps = this.displaySteps();
        const lastIndex = steps.length - 1;
        return steps.map((step, index) => {
            const isLast = index === lastIndex;
            const text = isLast
                ? step.message.slice(0, this.typedCount(step))
                : step.message;
            return { ...step, text, last: isLast };
        });
    });

    /** Alterna a expansão do bloco. */
    protected toggle(): void {
        this.expanded.update((value) => !value);
    }

    /** Caracteres já digitados de um passo (limitado ao tamanho da mensagem). */
    private typedCount(step: DisplayStep): number {
        return Math.min(
            this.typedLengths.get(step.name) ?? 0,
            step.message.length,
        );
    }

    /** Inicia o timer de digitação do último passo (idempotente). */
    private startTyping(): void {
        if (this.typingTimer !== null) {
            return;
        }
        this.typingTimer = setInterval(() => {
            const steps = this.displaySteps();
            const last = steps[steps.length - 1];
            if (!last || this.typedCount(last) >= last.message.length) {
                this.stopTyping();
                return;
            }
            this.typedLengths.set(last.name, this.typedCount(last) + 1);
            this.typingTick.update((value) => value + 1);
        }, StepsComponent.TYPING_INTERVAL_MS);
    }

    /** Interrompe o timer de digitação, se ativo. */
    private stopTyping(): void {
        if (this.typingTimer !== null) {
            clearInterval(this.typingTimer);
            this.typingTimer = null;
        }
    }

    /**
     * Escolhe a classe de ícone (Font Awesome) de um passo a partir do seu
     * `name` (prioritário) e, se nenhuma regra casar, da sua `message`.
     *
     * @param name Nome técnico do passo (ex.: `bm25_search`).
     * @param message Mensagem informativa do passo.
     * @returns Classe do ícone (ex.: `fa-magnifying-glass`).
     */
    protected iconFor(name: string, message: string): string {
        return (
            this.matchIcon((name ?? '').toLowerCase()) ??
            this.matchIcon((message ?? '').toLowerCase()) ??
            StepsComponent.DEFAULT_ICON
        );
    }

    /** Retorna o ícone da primeira regra cujas keywords casem no texto, ou `null`. */
    private matchIcon(text: string): string | null {
        if (!text) {
            return null;
        }
        for (const rule of StepsComponent.ICON_RULES) {
            if (rule.keywords.some((keyword) => text.includes(keyword))) {
                return rule.icon;
            }
        }
        return null;
    }

    /**
     * Formata um tempo decorrido em milissegundos para exibição.
     *
     * @param ms Tempo decorrido em milissegundos.
     * @returns `"{ms} ms"` para valores abaixo de 1s; caso contrário `"{s.d} s"`.
     */
    protected formatElapsed(ms: number): string {
        return ms < 1000
            ? `${Math.round(ms)} ms`
            : `${(ms / 1000).toFixed(1)} s`;
    }
}
